import {useQuery} from '@tanstack/react-query';
import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {IElementBase} from '../../../../incase-domain/elements/base/elementBase';
import {IElementQuestionnaireWithAnswers} from '../../../../incase-domain/elements/base/questionElementBase';
import {
    ElementInteraction,
    LikertScaleInteractions,
    MarkupImageElementInteractions,
    QuestionElementInteractions,
    QuestionMatchPairInteractions,
    SequenceQuestionInteraction,
    YesNoElementInteractions,
} from '../../../../incase-domain/proptypes/runfile';
import {fetchCodebookEntriesByIds} from '../../../../service/codebook.service';
import {runFileElementInteractionsSelector} from '../../../../store/selectors/runfile.selectors';
import useSequenceQuestionTry from './sequence/useSequenceQuestionTry';
import useMultiselectTry from './multiSelect/useMultiselectTry';
import useMatchPairTry from './matchPair/useMatchPairTry';
import useLikertScaleTry from './likertScale/useLikertScaleTry';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {IElementQuestionYesNo} from '../../../../incase-domain/elements/question/questionYesNo';
import {IElementSequenceQuestion} from '../../../../incase-domain/elements/question/questionSequence';
import {IElementQuestion} from '../../../../incase-domain/elements/question/question';
import {IElementPhotoAnswerQuestion} from '../../../../incase-domain/elements/question/photoAnswerQuestion';
import {IElementMultiSelect} from '../../../../incase-domain/elements/question/multiSelect';
import {IElementQuestionMatchPair} from '../../../../incase-domain/elements/question/questionMatchPair';
import {IElementLikertScale} from '../../../../incase-domain/elements/question/likertScale';
import useImageMarkupTry from './imageMarkup/useImageMarkupTry';
import {IElementImageMarkup} from '../../../../incase-domain/elements/question/imageMarkup';
import useQuestionTry from './useQuestionTry';
import {mapCodebookEntriesToAnswers} from './utils/codebook.utils';
import useYesNoQuestionActions from './yesNoQuestion/useYesNoQuestionTry';
import {IElementQuestionYesNoV2} from '../../../../incase-domain/elements/question/questionYesNoV2';

function useQuestionsTry(element?: IElementBase): (() => void) | ((answer?: number) => void) {
    const elementInteractionsBySlide = useSelector(runFileElementInteractionsSelector);

    const {confirmAnswer: yesNoTry} = useYesNoQuestionActions();
    const sequenceTry = useSequenceQuestionTry();
    const questionTry = useQuestionTry<IElementQuestion>();
    const yesNoV2Try = useQuestionTry<IElementQuestionYesNoV2>();
    const photoQuestionTry = useQuestionTry<IElementPhotoAnswerQuestion>();
    const multiselectTry = useMultiselectTry();
    const matchPairTry = useMatchPairTry();
    const likertScaleTry = useLikertScaleTry();
    const imageMarkupTry = useImageMarkupTry();

    const elementInteractions = useMemo(() => {
        if (!element) return {} as ElementInteraction;

        const slideElementInteractions = elementInteractionsBySlide ? elementInteractionsBySlide[element.slideId] : {};
        if (slideElementInteractions && slideElementInteractions[element.id]) {
            return slideElementInteractions[element.id] as ElementInteraction;
        }

        return {} as ElementInteraction;
    }, [elementInteractionsBySlide, element]);

    const {data: markedCodebookAnswers} = useQuery(
        ['markedCodebookEntries', (elementInteractions as QuestionElementInteractions).markedAnswersIds],
        () =>
            (elementInteractions as QuestionElementInteractions).markedAnswersIds &&
            fetchCodebookEntriesByIds((elementInteractions as QuestionElementInteractions).markedAnswersIds as string[]),
        {
            enabled:
                !!element &&
                'codebookId' in element &&
                !!(elementInteractions as QuestionElementInteractions).markedAnswersIds &&
                ((elementInteractions as QuestionElementInteractions).markedAnswersIds as string[]).length > 0,
            select: (data) => (data ? mapCodebookEntriesToAnswers(element as IElementQuestionnaireWithAnswers, data) : []),
        }
    );

    if (!element)
        return () => {
            throw new Error('[useQuestionsTry]: element is undefined');
        };

    switch (element.type) {
        case ELEMENT.YES_NO_QUESTION:
            return () => yesNoTry(element as IElementQuestionYesNo, elementInteractions as YesNoElementInteractions);
        case ELEMENT.YES_NO_QUESTION_V2:
            return () => yesNoV2Try(element as IElementQuestionYesNoV2, elementInteractions as QuestionElementInteractions);
        case ELEMENT.SEQUENCE_QUESTION:
            return () => sequenceTry(element as IElementSequenceQuestion, elementInteractions as SequenceQuestionInteraction);
        case ELEMENT.QUESTION:
            return () => questionTry(element as IElementQuestion, elementInteractions as QuestionElementInteractions);
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            return () => photoQuestionTry(element as IElementPhotoAnswerQuestion, elementInteractions as QuestionElementInteractions);
        case ELEMENT.MULTI_SELECT:
            return () =>
                multiselectTry(element as IElementMultiSelect, elementInteractions as QuestionElementInteractions, markedCodebookAnswers);
        case ELEMENT.MATCH_PAIR_QUESTION:
            return () => matchPairTry(element as IElementQuestionMatchPair, elementInteractions as QuestionMatchPairInteractions);
        case ELEMENT.LIKERT_SCALE:
            return () => likertScaleTry(element as IElementLikertScale, elementInteractions as LikertScaleInteractions);
        case ELEMENT.IMAGE_MARKUP:
            return () => imageMarkupTry(element as IElementImageMarkup, elementInteractions as MarkupImageElementInteractions);
        default:
            return () => {
                throw new Error('[useQuestionsTry]: element try is undefined');
            };
    }
}

export default useQuestionsTry;
